import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentType } from 'preact';
import { BadgeCheck, Gift, Sparkles, Ticket } from 'lucide-preact';
import type {
  CoasterRuntime,
  RuntimeMediaState,
  RuntimeMonetizationState,
} from '../app/runtime-types.ts';
import { EXPORT_RIDE_AUDIO_AVAILABLE, type MediaArtifactKind } from '../media/index.ts';
import { CloseButton, Disclosure } from './primitives.tsx';
import { UI_COPY } from './ui-copy.ts';

type MediaTab = 'photo' | 'highlight' | 'overview';
type OverviewKind = 'photo' | 'video';

function tabArtifact(tab: MediaTab, overviewKind: OverviewKind): MediaArtifactKind {
  if (tab === 'highlight') return 'highlight_video';
  if (tab === 'overview') return overviewKind === 'photo' ? 'share_card' : 'animated_loop';
  return 'photo';
}

export function MediaStudio({
  state,
  runtime,
  title,
  ReactionOptions,
  monetization,
}: {
  readonly state: RuntimeMediaState;
  readonly runtime: CoasterRuntime;
  readonly title: string;
  readonly monetization: RuntimeMonetizationState;
  readonly ReactionOptions?: ComponentType<{
    state: RuntimeMediaState;
    runtime: CoasterRuntime;
    busy: boolean;
    compact?: boolean;
  }>;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const [tab, setTab] = useState<MediaTab>(() =>
    state.videoUrl ? 'highlight' : state.loopUrl ? 'overview' : 'photo',
  );
  const [overviewKind, setOverviewKind] = useState<OverviewKind>(() =>
    state.loopUrl ? 'video' : 'photo',
  );
  const artifact = tabArtifact(tab, overviewKind);
  const artifactReady =
    tab === 'photo'
      ? Boolean(state.photoUrl)
      : tab === 'highlight'
        ? Boolean(state.videoUrl)
        : overviewKind === 'photo'
          ? Boolean(state.cardUrl)
          : Boolean(state.loopUrl);
  const busy = state.status === 'preparing' || state.status === 'encoding';
  const showStatus =
    busy ||
    state.status === 'error' ||
    state.status === 'cancelled' ||
    state.status === 'static_only';
  const estimatedSeconds = Math.max(
    1,
    Math.ceil((1 - state.progress) * (tab === 'overview' && overviewKind === 'video' ? 5 : 18)),
  );
  const selectedOffer = monetization.offers.find(
    (offer) => offer.id === monetization.selectedOfferId,
  );
  const selectedOfferAccessible = Boolean(
    selectedOffer &&
    (monetization.passOwned || monetization.sessionGrants.includes(selectedOffer.id)),
  );
  const transactionBusy =
    monetization.transactionState === 'processing' || monetization.transactionState === 'checking';
  const visibleOffers = monetization.offers.filter(
    (offer) =>
      offer.id !== 'coaster_pass' ||
      monetization.passOwned ||
      monetization.premiumIntentCount >= 2 ||
      monetization.selectedOfferId === 'coaster_pass',
  );

  useEffect(() => {
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.querySelector<HTMLElement>('button, [href], input, select, textarea')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        runtime.closeMediaStudio();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [runtime]);

  return (
    <div class="modal-backdrop media-studio-backdrop" role="presentation">
      <section
        ref={dialogRef}
        class={`media-studio${tab === 'overview' ? ' is-overview' : ''}${showStatus ? ' has-status' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-studio-title"
        data-testid="media-studio"
      >
        <header class="media-studio-header">
          <div>
            <h2 id="media-studio-title">{UI_COPY.media.title}</h2>
            <span class="media-studio-coaster-name">{title}</span>
          </div>
          <CloseButton label="Close Ride Media" onClick={() => runtime.closeMediaStudio()} />
        </header>

        <div class="media-tabs" role="tablist" aria-label="Media format">
          {(
            [
              ['photo', UI_COPY.media.photo],
              ['highlight', state.videoUrl ? 'Video · Ready' : 'Video'],
              ['overview', 'Coaster Overview'],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              role="tab"
              aria-selected={tab === value}
              class={tab === value ? 'active' : ''}
              onClick={() => setTab(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div class="media-overview-tabs" role="tablist" aria-label="Coaster Overview format">
            <button
              type="button"
              role="tab"
              aria-selected={overviewKind === 'photo'}
              class={overviewKind === 'photo' ? 'active' : ''}
              onClick={() => setOverviewKind('photo')}
            >
              Overview Photo
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={overviewKind === 'video'}
              class={overviewKind === 'video' ? 'active' : ''}
              onClick={() => setOverviewKind('video')}
            >
              {state.loopUrl ? 'Overview Video · Ready' : 'Overview Video'}
            </button>
          </div>
        )}

        <div class="media-preview" data-testid={`media-preview-${tab}`}>
          {tab === 'photo' && state.photoUrl && (
            <img src={state.photoUrl} alt={`Action photo from ${title}`} />
          )}
          {tab === 'overview' && overviewKind === 'photo' && state.cardUrl && (
            <img src={state.cardUrl} alt={`Coaster overview for ${title}`} />
          )}
          {tab === 'highlight' && state.videoUrl && (
            <video src={state.videoUrl} controls playsInline preload="metadata" />
          )}
          {tab === 'overview' && overviewKind === 'video' && state.loopUrl && (
            <video src={state.loopUrl} controls playsInline loop muted preload="metadata" />
          )}
          {!artifactReady && (
            <div class="media-placeholder">
              <strong>
                {busy
                  ? 'Working on it…'
                  : tab === 'highlight'
                    ? 'Ready to make your video'
                    : tab === 'overview' && overviewKind === 'video'
                      ? 'Ready to make a loop'
                      : tab === 'photo'
                        ? 'Preparing Action Photo'
                        : 'Preparing Coaster Overview'}
              </strong>
              <span>
                {tab === 'highlight'
                  ? 'A 15-second vertical replay with your best moments.'
                  : tab === 'overview' && overviewKind === 'video'
                    ? 'A silent eight-second orbit around your complete coaster.'
                    : tab === 'photo'
                      ? 'A portrait action image for social feeds.'
                      : 'A landscape overview for links and listings.'}
              </span>
            </div>
          )}
        </div>

        {(tab === 'photo' || (tab === 'overview' && overviewKind === 'photo')) && (
          <p class="media-artifact-purpose">
            {tab === 'photo'
              ? 'Portrait action photo for sharing a memorable ride moment.'
              : 'Landscape overview showing the complete coaster for links and listings.'}
          </p>
        )}

        {showStatus && (
          <div class="media-status" aria-live="polite">
            <span>{state.message}</span>
            {state.status === 'encoding' && (
              <>
                <progress max="1" value={state.progress} aria-label="Video generation progress" />
                <small>About {estimatedSeconds} seconds remaining on this device.</small>
              </>
            )}
          </div>
        )}

        {tab === 'photo' && state.posterCandidates.length > 0 && (
          <div class="poster-choices" role="group" aria-label="Photo moment">
            {state.posterCandidates.map((candidate) => (
              <button
                type="button"
                class={state.selectedPosterId === candidate.id ? 'active' : ''}
                aria-pressed={state.selectedPosterId === candidate.id}
                disabled={busy}
                onClick={() => void runtime.selectPosterCandidate(candidate.id)}
              >
                {candidate.thumbnailUrl ? (
                  <img src={candidate.thumbnailUrl} alt="" aria-hidden="true" />
                ) : (
                  <span class="poster-thumb-placeholder" aria-hidden="true" />
                )}
                <span>{candidate.label}</span>
              </button>
            ))}
            {state.creatorPosterSkipped && (
              <small class="highlight-choice-description media-poster-note">
                The creator marker was too slow or too close to the station, so a stronger action
                moment was selected.
              </small>
            )}
          </div>
        )}

        {tab === 'highlight' && (
          <div class="media-options">
            <div class="highlight-choices" role="group" aria-label="Highlight edit">
              {([0, 1, 2, 3] as const)
                .filter((value) => value !== 3 || monetization.activeTreatments.freshHighlightMix)
                .map((value) => (
                  <button
                    type="button"
                    aria-pressed={state.selectedAlternative === value}
                    onClick={() => runtime.selectHighlightAlternative(value)}
                    disabled={busy}
                  >
                    {state.editLabels[value]}
                  </button>
                ))}
            </div>
            <label class="toggle-row">
              <input
                type="checkbox"
                checked={state.includeAudio}
                onChange={(event) => runtime.setMediaAudioEnabled(event.currentTarget.checked)}
                disabled={busy || !EXPORT_RIDE_AUDIO_AVAILABLE}
              />
              <span>Include Ride Audio</span>
            </label>
            {!EXPORT_RIDE_AUDIO_AVAILABLE && (
              <small class="media-audio-unavailable">
                Ride audio is not available in this build.
              </small>
            )}
            {state.reactionRawUrl && ReactionOptions && (
              <ReactionOptions state={state} runtime={runtime} busy={busy} />
            )}
          </div>
        )}

        {monetization.sandbox && (
          <details class="premium-styles">
            <summary>
              <span class="premium-styles-icon" aria-hidden="true">
                <Sparkles size={18} />
              </span>
              <div>
                <strong>More Styles</strong>
                <small>Optional QA sandbox extras. Every current media format stays free.</small>
              </div>
              <span class="qa-balance">
                {monetization.availability.state === 'available'
                  ? `${monetization.wallet?.balance ?? 0} Credits`
                  : 'Offline'}
              </span>
            </summary>

            {monetization.availability.state !== 'available' ? (
              <div class="premium-offer-detail">
                <strong>Optional styles are offline right now</strong>
                <p>{monetization.availability.reason}</p>
                <p>Your free media and Download are still ready.</p>
                <button
                  class="tertiary-action"
                  type="button"
                  onClick={() => runtime.selectPremiumOffer(null)}
                >
                  Keep Free Version
                </button>
              </div>
            ) : (
              <div class="premium-offer-list" role="list" aria-label="Optional styles">
                {visibleOffers.map((offer) => {
                  const granted =
                    monetization.passOwned || monetization.sessionGrants.includes(offer.id);
                  const selected = monetization.selectedOfferId === offer.id;
                  return (
                    <button
                      type="button"
                      class={selected ? 'selected' : ''}
                      aria-pressed={selected}
                      disabled={transactionBusy}
                      onClick={() => runtime.selectPremiumOffer(selected ? null : offer.id)}
                    >
                      {offer.id === 'coaster_pass' ? (
                        <Ticket aria-hidden="true" size={18} />
                      ) : granted ? (
                        <BadgeCheck aria-hidden="true" size={18} />
                      ) : (
                        <Gift aria-hidden="true" size={18} />
                      )}
                      <span>
                        <strong>{offer.title}</strong>
                        <small>
                          {offer.id === 'coaster_pass'
                            ? `${offer.priceCredits} Credits · restores in this QA profile`
                            : granted
                              ? 'Ready while this coaster stays open'
                              : `${offer.priceCredits} Credits${offer.rewardedAdAvailable ? ' or optional ad' : ''}`}
                        </small>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedOffer && (
              <div class="premium-offer-detail">
                <strong>{selectedOffer.title}</strong>
                <p>{selectedOffer.description}</p>
                {selectedOffer.id === 'passenger_cam' && !monetization.passengerCamEligible && (
                  <small>
                    Complete a Reaction Cam ride first. The free corner pane is unchanged.
                  </small>
                )}
                <div class="premium-offer-actions">
                  {selectedOffer.id === 'coaster_pass' && monetization.passOwned ? (
                    <button class="secondary-action" type="button" disabled>
                      Pass Active
                    </button>
                  ) : selectedOfferAccessible ? (
                    <button
                      class="primary-action"
                      type="button"
                      disabled={
                        transactionBusy ||
                        (selectedOffer.id === 'passenger_cam' && !monetization.passengerCamEligible)
                      }
                      onClick={() => void runtime.applyPremiumOffer(selectedOffer.id)}
                    >
                      Apply Style
                    </button>
                  ) : (
                    <>
                      <button
                        class="primary-action"
                        type="button"
                        disabled={
                          transactionBusy ||
                          (selectedOffer.id === 'passenger_cam' &&
                            !monetization.passengerCamEligible)
                        }
                        onClick={() =>
                          void runtime.acquirePremiumOffer(selectedOffer.id, 'credits')
                        }
                      >
                        Use {selectedOffer.priceCredits} Credits
                      </button>
                      {selectedOffer.rewardedAdAvailable && (
                        <button
                          class="secondary-action"
                          type="button"
                          disabled={transactionBusy}
                          onClick={() =>
                            void runtime.acquirePremiumOffer(selectedOffer.id, 'rewarded_ad')
                          }
                        >
                          Watch Optional Ad
                        </button>
                      )}
                    </>
                  )}
                  <button
                    class="tertiary-action"
                    type="button"
                    disabled={transactionBusy}
                    onClick={() => runtime.selectPremiumOffer(null)}
                  >
                    Keep Free Version
                  </button>
                </div>
                <small class="premium-session-note">
                  Standard extras last while this coaster remains open, including retries.
                </small>
              </div>
            )}

            <div class="premium-status" aria-live="polite">
              {transactionBusy && <span class="premium-spinner" aria-hidden="true" />}
              <span>{monetization.message}</span>
            </div>

            <Disclosure summary="QA sandbox controls">
              <div class="premium-qa-controls">
                <label>
                  Simulated result
                  <select
                    disabled={transactionBusy}
                    onChange={(event) =>
                      runtime.setMonetizationSandboxScenario(
                        event.currentTarget.value as
                          | 'success'
                          | 'insufficient_balance'
                          | 'cancelled'
                          | 'delayed_success'
                          | 'duplicate_response'
                          | 'offline'
                          | 'revoked',
                      )
                    }
                  >
                    <option value="success">Success</option>
                    <option value="insufficient_balance">Insufficient balance</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="delayed_success">Delayed success</option>
                    <option value="duplicate_response">Duplicate response</option>
                    <option value="offline">Offline</option>
                    <option value="revoked">Revoked</option>
                  </select>
                </label>
                <button
                  class="tertiary-action"
                  type="button"
                  disabled={transactionBusy}
                  onClick={() => void runtime.restorePremiumEntitlements()}
                >
                  Restore QA Purchases
                </button>
              </div>
            </Disclosure>
          </details>
        )}

        <div class="media-actions">
          {tab === 'highlight' && !state.videoUrl && (
            <button
              class="primary-action"
              type="button"
              disabled={busy || !state.formatLabel || !state.videoAvailable}
              onClick={() => void runtime.generateHighlightVideo()}
            >
              Generate Video{state.formatLabel ? ` (${state.formatLabel})` : ''}
            </button>
          )}
          {tab === 'overview' && overviewKind === 'video' && !state.loopUrl && (
            <button
              class="primary-action"
              type="button"
              disabled={busy || !state.formatLabel || !state.videoAvailable}
              onClick={() => void runtime.generateAnimatedLoop()}
            >
              Generate Loop
            </button>
          )}
          {state.status === 'encoding' && (
            <button
              class="secondary-action"
              type="button"
              onClick={() => runtime.cancelMediaExport()}
            >
              Cancel
            </button>
          )}
          {artifactReady && (
            <>
              <button
                class="primary-action"
                type="button"
                onClick={() => runtime.downloadMedia(artifact)}
              >
                Download
              </button>
              {state.imageCopyAvailable &&
                (tab === 'photo' || (tab === 'overview' && overviewKind === 'photo')) && (
                  <button
                    class="secondary-action"
                    type="button"
                    onClick={() => void runtime.copyMediaImage(artifact)}
                  >
                    Copy Image
                  </button>
                )}
            </>
          )}
          {state.publicLinkAvailable && (
            <button
              class="secondary-action"
              type="button"
              onClick={() => void runtime.copyMediaRideLink()}
            >
              Copy Ride Link
            </button>
          )}
        </div>

        <Disclosure summary="Privacy & storage">
          <p class="media-privacy-note">
            Created on this device and never uploaded by Buncha Coasters. Generated videos and loops
            stay ready while this coaster remains open. Reaction Cam recordings are removed when you
            leave this coaster, reload, or close the tab.
            {!state.publicLinkAvailable && (
              <> Private links are never placed inside exported media.</>
            )}
          </p>
        </Disclosure>
        {monetization.cheerPulse > 0 && (
          <div class="premium-cheer" key={monetization.cheerPulse} aria-live="polite">
            <span aria-hidden="true">★</span>
            Cheer! Cosmetic only—no ranking effect.
          </div>
        )}
      </section>
    </div>
  );
}
